import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  IDistributorDocument,
  IDocumentImageChange,
} from '../../../interfaces/distributor.interface';
import { PageLink } from '@metronic/layout';
import { ICardDetailBody } from '@models/card.model';
import { BaseService } from '@services/base-service.service';
import { ActivatedRoute } from '@angular/router';
import { UtilitiesService } from '@services/utilities.service';
import { AuthService } from '@services/auth.service';
import { RolePrivilegeService } from '@services/role-privilege.service';
import { EnumRole } from '@models/role.model';
import { API } from '@config/constants/api.constant';
import { IDetailInformasi_DistributorDocument } from '../../../interfaces/detail-informasi.interface';
import { IGenericNameUrl } from '@shared/interface/generic';
import { DistributorService } from '../../../distributor.service';
import { DistributorStatusEnum } from '@config/enum/distributor.enum';

@Component({
  selector: 'app-tab-dokumen',
  templateUrl: './tab-dokumen.component.html',
  styleUrls: ['./tab-dokumen.component.scss'],
})
export class TabDokumenComponent implements OnInit {
  id: any | null = null;
  status: string | null = '';
  detail = new BehaviorSubject({} as IDistributorDocument);
  links: Array<PageLink>;
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  detailDocumentDistributor: ICardDetailBody[];
  detailDocumentNpwp: ICardDetailBody[];
  detailDocumentActivation: ICardDetailBody[];
  keyDetailPrivilege: string | null = 'DETAIL_DISTRIBUTOR';
  privilegeConfidentialDocuments: Array<any>;
  documentNonActive: IGenericNameUrl[] = [];

  @Input() isEditRequest = false;
  documentImageChange = new BehaviorSubject([] as IDocumentImageChange[]);

  isFinance = false;

  get ShowDocumentNonActive() {
    return this.distributorService.DistributorDetailHeader.status_enum === DistributorStatusEnum.NON_ACTIVATED || this.distributorService.DistributorDetailHeader.status_enum === DistributorStatusEnum.REJECT_NONACTIVATED;
  }

  constructor(
    private baseService: BaseService,
    public utilities: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private authService: AuthService,
    private rolePrivilegeService: RolePrivilegeService,
    private distributorService: DistributorService
  ) {
  }

  ngOnInit(): void {
    this.isFinance = this.authService.currentUserRole === EnumRole.FINANCE;
    this.activatedRoute.paramMap.subscribe((params) => {
      this.id = params.get('id');
      this.status = params.get('status');
    });
    if (this.id) {
      this.privilegeConfidentialDocuments = this.rolePrivilegeService.checkPrivilegeConfidential('DISTRIBUTOR', this.keyDetailPrivilege, 'TAB_DOCUMENTS');
      this.getDetailInfo();
      this.initPageInfo();
    }
  }

  getDetailInfo() {
    const _snapshotSource = this.activatedRoute.snapshot.queryParams.source;
    let API_DETAIL_DOCUMENT = API.DETAIL_DOCUMENT;

    if (this.isEditRequest) {
      API_DETAIL_DOCUMENT = _snapshotSource === 'log' ? API.DETAIL_DOCUMENT_EDIT_REQUEST_LOG : API.DETAIL_DOCUMENT_EDIT_REQUEST;
    }

    this.baseService.getData<IDistributorDocument>(API_DETAIL_DOCUMENT + this.id).subscribe((value) => {
      if (value) {
        this.detail.next(value.data);
        this.documentNonActive = value.data.document_non_active ? [value.data.document_non_active] : [{} as IGenericNameUrl];
        this.isLoading.next(false);
        this.ref.detectChanges();
      }
    });
  }

  initPageInfo() {
    this.detail.subscribe((value) => {
      if (!Object.keys(value).length) {
        return;
      }

      if (this.isEditRequest) {
        return this.handleDataEditRequest(value);
      }

      this.mapDocumentLabelValue(value);
    });
  }

  mapDocumentLabelValue(value: IDistributorDocument): void {
    const {
      owner_ktp_image_url,
      deed_incorporation_image_url,
      deed_amendment_image_url,
      npwp_image_url,
      nib_image_url,
      place_business_image_url,
      pkp_image_url,
      ktp_manager_image_url,
      assigned_kul_image_url,
      other_image_url,
      distributor_assigned_kul_image_url,
      // ktp_chargeable_image_url,
      integrity_pact_image_url,
    } = value;

    const createDocumentArray = (entries: {
      label: string;
      name: string;
      value: string | null | undefined
    }[]) => entries.filter((entry) => !!entry.value); // Optional: filter out null/undefined

    this.detailDocumentDistributor = this.utilities.privilegeConfidentialDocuments(
      this.privilegeConfidentialDocuments,
      createDocumentArray([
        { label: 'KTP Pemilik Usaha :', name: 'CARD_PIC_KTP_PEMILIK', value: owner_ktp_image_url },
        { label: 'KTP Pengelola Usaha :', name: 'CARD_PIC_KTP_PENGELOLA', value: ktp_manager_image_url },
        { label: 'Akta Pendirian:', name: 'CARD_PIC_AKTA_PENDIRIAN', value: deed_incorporation_image_url },
        { label: 'Akta Perubahan :', name: 'CARD_PIC_AKTA_PERUBAHAN', value: deed_amendment_image_url },
        { label: 'NIB :', name: 'CARD_PIC_NIB', value: nib_image_url },
        { label: 'Foto Tempat Usaha :', name: 'CARD_PIC_TEMPAT_USAHA', value: place_business_image_url },
      ]),
    );

    this.detailDocumentNpwp = this.utilities.privilegeConfidentialDocuments(
      this.privilegeConfidentialDocuments,
      createDocumentArray([
        { label: 'NPWP :', name: 'CARD_PIC_NPWP', value: npwp_image_url },
        { label: 'Surat Pernyataan non PKP :', name: 'CARD_PIC_NON_PKP', value: pkp_image_url },
      ]),
    );

    this.detailDocumentActivation = createDocumentArray([
      { label: 'KUL :', name: 'CARD_PIC_KUL', value: assigned_kul_image_url },
      { label: 'Foto TTD KUL :', name: 'CARD_PIC_SIGNING_KUL', value: distributor_assigned_kul_image_url },
      { label: 'Pakta Integritas :', name: 'CARD_PIC_INTEGRITY_PACT', value: integrity_pact_image_url },
      { label: 'Lainnya :', name: 'CARD_PIC_OTHER', value: other_image_url },
    ]);
  }

  handleDataEditRequest(_detail: IDistributorDocument) {
    const data = _detail as IDetailInformasi_DistributorDocument;
    const { distributor_document_new_data, distributor_document_change } = data;
    this.documentImageChange.next(distributor_document_change);
    this.mapDocumentLabelValue(distributor_document_new_data);
  }
}
